import type { Citation } from "../../citations";
import type { SkillRunContext } from "./skills";
import { withPlanningEvidenceSkillBinding, type PlanningEvidencePack } from "./planningEvidence";

export interface LibrarySlideCandidate {
  slideId: string;
  title: string;
  description?: string;
  tags?: string[];
  thumbnailUrl?: string;
  pptxUrl?: string;
  pptxUrlCandidates?: string[];
  rankReason?: string;
  rankScore?: number;
}

export interface SlideOutline {
  index: number;
  title: string;
  bullets: string[];
  baseSlideIndex?: number;
  librarySlideId?: string;
  librarySlideTitle?: string;
  librarySlideThumbnailUrl?: string;
  librarySlidePptxUrl?: string;
  librarySlidePptxUrlCandidates?: string[];
  librarySlideTags?: string[];
  librarySlideDescription?: string;
  rememberedLibrarySlide?: LibrarySlideCandidate;
  librarySlideCandidates?: LibrarySlideCandidate[];
}

export interface DeckPlan {
  topic: string;
  slideCount: number;
  slides: SlideOutline[];
  /** Opaque in-memory identity for one chat -> Review -> build operation. */
  reviewOperationId?: string;
  /** Monotonic host-only generation for serialized updates to one Review. */
  reviewOperationGeneration?: number;
  /**
   * Host-only Skill binding state. `bound` requires `skillRunContext`;
   * `unbound` and `cleared` must not carry one.
   */
  reviewSkillBindingState?: ReviewSkillBindingState;
  /**
   * Transient operation-scoped context for an active Review flow. Callers must
   * not serialize the full guidance into dialog or local-storage payloads.
   */
  skillRunContext?: SkillRunContext;
  /** Bounded factual research captured while this Review operation was planned. */
  planningEvidence?: PlanningEvidencePack;
  /** Langfuse run id for the planner turn that produced this plan. */
  plannerRunId?: string;
  plannerCountAdjusted?: boolean;
  plannerRetries?: number;
  plannerOriginalCount?: number;
  groundingMode?: "run_output" | "metadata_only" | "none";
  groundingContextForSlides?: string;
  groundingMessage?: string;
  /**
   * API-side citation envelopes carried over from the task output that
   * grounded this deck. Forwarded to the deck-wide citation pool at
   * `createDeck` time so newly generated slides can resolve any inline
   * `<u id="…">` markup back to a `Citation` record, and so the per-slide
   * "Citations" toggle in `SlideCitationsView` has a non-empty source
   * pool to fall back on for slides that don't yet have chat history.
   */
  groundingCitations?: Citation[];
}

export type ReviewSkillBindingState = "unbound" | "bound" | "cleared";

export interface DeckPlanTransientContext {
  readonly reviewOperationId: string;
  readonly reviewOperationGeneration: number;
  readonly reviewSkillBindingState: ReviewSkillBindingState;
  readonly skillRunContext?: SkillRunContext;
  readonly planningEvidence?: PlanningEvidencePack;
}

/** Removes every operation-private field before dialog, storage, or plan JSON transport. */
export function stripDeckPlanTransientContext(plan: DeckPlan): DeckPlan {
  const sanitized = { ...plan };
  delete sanitized.reviewOperationId;
  delete sanitized.reviewOperationGeneration;
  delete sanitized.reviewSkillBindingState;
  delete sanitized.skillRunContext;
  delete sanitized.planningEvidence;
  return sanitized;
}

export function getDeckPlanTransientContext(
  plan: DeckPlan | null | undefined
): DeckPlanTransientContext | undefined {
  const reviewOperationId = plan?.reviewOperationId?.trim();
  if (!reviewOperationId) return undefined;
  const suppliedGeneration = plan?.reviewOperationGeneration;
  if (
    suppliedGeneration !== undefined &&
    (!Number.isSafeInteger(suppliedGeneration) || suppliedGeneration < 0)
  ) {
    throw new Error("Deck plan Review generation must be a non-negative safe integer.");
  }
  const reviewOperationGeneration = suppliedGeneration ?? 0;
  const reviewSkillBindingState =
    plan?.reviewSkillBindingState ?? (plan?.skillRunContext ? "bound" : "unbound");
  if (reviewSkillBindingState === "bound" && !plan?.skillRunContext) {
    throw new Error("A bound Review Skill state requires Skill context.");
  }
  if (reviewSkillBindingState !== "bound" && plan?.skillRunContext) {
    throw new Error("An unbound or cleared Review Skill state cannot carry Skill context.");
  }
  if (plan?.planningEvidence && plan.planningEvidence.reviewOperationId !== reviewOperationId) {
    throw new Error("Deck plan evidence is bound to a different Review operation.");
  }
  return Object.freeze({
    reviewOperationId,
    reviewOperationGeneration,
    reviewSkillBindingState,
    ...(plan?.skillRunContext ? { skillRunContext: plan.skillRunContext } : undefined),
    ...(plan?.planningEvidence ? { planningEvidence: plan.planningEvidence } : undefined),
  });
}

/** Reattaches a host-owned private snapshot to a dialog-safe plan. */
export function reattachDeckPlanTransientContext(
  plan: DeckPlan,
  context: DeckPlanTransientContext | null | undefined
): DeckPlan {
  const sanitized = stripDeckPlanTransientContext(plan);
  if (!context) return sanitized;
  if (
    !Number.isSafeInteger(context.reviewOperationGeneration) ||
    context.reviewOperationGeneration < 0
  ) {
    throw new Error("Deck plan Review generation must be a non-negative safe integer.");
  }
  if (context.reviewSkillBindingState === "bound" && !context.skillRunContext) {
    throw new Error("A bound Review Skill state requires Skill context.");
  }
  if (context.reviewSkillBindingState !== "bound" && context.skillRunContext) {
    throw new Error("An unbound or cleared Review Skill state cannot carry Skill context.");
  }
  if (
    context.planningEvidence &&
    context.planningEvidence.reviewOperationId !== context.reviewOperationId
  ) {
    throw new Error("Deck plan evidence is bound to a different Review operation.");
  }
  return {
    ...sanitized,
    reviewOperationId: context.reviewOperationId,
    reviewOperationGeneration: context.reviewOperationGeneration,
    reviewSkillBindingState: context.reviewSkillBindingState,
    ...(context.skillRunContext ? { skillRunContext: context.skillRunContext } : undefined),
    ...(context.planningEvidence ? { planningEvidence: context.planningEvidence } : undefined),
  };
}

/**
 * Freezes the private snapshot at confirmation. Provisional evidence is
 * intentionally discarded unless the approved plan has an active Skill.
 */
export function freezeApprovedDeckPlanContext(
  plan: DeckPlan,
  context: DeckPlanTransientContext | null | undefined
): DeckPlan {
  const sanitized = stripDeckPlanTransientContext(plan);
  if (context?.reviewSkillBindingState !== "bound" || !context.skillRunContext) {
    return sanitized;
  }
  const planningEvidence = context.planningEvidence
    ? withPlanningEvidenceSkillBinding(
        context.planningEvidence,
        context.skillRunContext.skills?.map((skill) => skill.id),
        context.skillRunContext.guidanceChars
      )
    : undefined;
  return reattachDeckPlanTransientContext(sanitized, {
    reviewOperationId: context.reviewOperationId,
    reviewOperationGeneration: context.reviewOperationGeneration,
    reviewSkillBindingState: "bound",
    skillRunContext: context.skillRunContext,
    ...(planningEvidence ? { planningEvidence } : undefined),
  });
}
